// src/features/analytics/analyticsApi.ts
import { api } from "../auth/authApi";
import type {
  ProviderSummaryResponse,
  ProviderSummaryParams,
  ClientSummaryResponse,
  ClientSummaryParams,
} from "./types";

// remove empty query params
const cleanParams = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  link.setAttribute("target", "_blank");

  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }, 100);
};

export const analyticsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    providerSummary: builder.query<ProviderSummaryResponse, ProviderSummaryParams>({
      query: (params) => ({
        url: "/analytics/providers/summary/",
        method: "GET",
        params: cleanParams({
          date_from: params.date_from,
          date_to: params.date_to,
          provider: params.provider,
          include_all_statuses: params.include_all_statuses ? "true" : "false",
        }),
      }),
      providesTags: (_r) => [{ type: "Analytics" as const, id: "PROVIDER_SUMMARY" }],
    }),
    // XLSX Export (returns Blob)
    exportProviderSummaryXlsx: builder.mutation<
      Blob,
      {
        date_from: string;
        date_to: string;
        provider?: string;
        include_all_statuses?: boolean;
      }
    >({
      query: (params) => ({
        url: "/analytics/providers/summary.xlsx/", // backend route (trailing slash)
        method: "GET",
        params: cleanParams({
          date_from: params.date_from,
          date_to: params.date_to,
          provider: params.provider,
          include_all_statuses: params.include_all_statuses ? "true" : "false",
        }),
        // ✅ RTK Query fetchBaseQuery supports responseHandler
        responseHandler: (response) => response.blob(),
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data: blob } = await queryFulfilled;

          const filename = `provider_summary_${arg.date_from}_${arg.date_to}.xlsx`;
          downloadBlob(blob, filename);
        } catch {
          // leave UI to show snackbar error if you want
        }
      },
    }),

    clientSummary: builder.query<ClientSummaryResponse, ClientSummaryParams>({
      query: (params) => ({
        url: "/analytics/clients/summary/",
        method: "GET",
        params: cleanParams({
          date_from: params.date_from,
          date_to: params.date_to,
          client: params.client,
          include_all_statuses: params.include_all_statuses ? "true" : "false",
        }),
      }),
      providesTags: (_r) => [{ type: "Analytics" as const, id: "CLIENT_SUMMARY" }],
    }),
    exportClientSummaryXlsx: builder.mutation<
      Blob,
      {
        date_from: string;
        date_to: string;
        client?: string;
        include_all_statuses?: boolean;
      }
    >({
      query: (params) => ({
        url: "/analytics/clients/summary.xlsx/",
        method: "GET",
        params: cleanParams({
          date_from: params.date_from,
          date_to: params.date_to,
          client: params.client,
          include_all_statuses: params.include_all_statuses ? "true" : "false",
        }),
        responseHandler: (response) => response.blob(),
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data: blob } = await queryFulfilled;
          const filename = `client_summary_${arg.date_from}_${arg.date_to}.xlsx`;
          downloadBlob(blob, filename);
        } catch {
          // leave UI to show error if needed
        }
      },
    }),
  }),
  overrideExisting: false,
});


export const {
  useProviderSummaryQuery,
  useLazyProviderSummaryQuery,
  useExportProviderSummaryXlsxMutation,
  useClientSummaryQuery,
  useLazyClientSummaryQuery,
  useExportClientSummaryXlsxMutation,
} = analyticsApi;
